import { useState, useEffect, useCallback, useMemo } from "react";
import { api, formatApiError, API_BASE } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { FolderOpen, Plus, Search, Trash2, Download, FileText, File as FileIcon, UploadCloud, Sparkles, Loader2 } from "lucide-react";
import { FilePreviewModal } from "../components/chat/FilePreview";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Avatar } from "../components/ui-bits";
import { formatDistanceToNow } from "date-fns";
import { cn } from "../lib/utils";

const TYPE_FILTERS = [
  { key: "all", label: "All types" },
  { key: "image", label: "Photos" },
  { key: "pdf", label: "PDFs" },
  { key: "other", label: "Other" },
];

const CATEGORY_FILTERS = [
  { key: "all", label: "Semua" },
  { key: "spk", label: "SPK/SPKK" },
  { key: "invoice", label: "Invoice" },
  { key: "receipt", label: "Kuitansi" },
  { key: "payment", label: "Payment" },
  { key: "project", label: "Project" },
  { key: "upload", label: "User Upload" },
  { key: "chat", label: "Chat" },
];

const CAT_META = {
  spk: { label: "SPK", cls: "bg-[#1B2A6B]/10 text-[#1B2A6B] dark:text-blue-300" },
  invoice: { label: "Invoice", cls: "bg-blue-500/10 text-blue-600 dark:text-blue-400" },
  receipt: { label: "Kuitansi", cls: "bg-[#10B981]/10 text-[#10B981]" },
  payment: { label: "Payment", cls: "bg-amber-500/10 text-amber-600 dark:text-amber-400" },
  project: { label: "Project", cls: "bg-muted text-muted-foreground" },
  upload: { label: "Upload", cls: "bg-muted text-muted-foreground" },
  chat: { label: "Chat", cls: "bg-teal-500/10 text-teal-600 dark:text-teal-400" },
};

function typeOf(f) {
  if (f.content_type?.startsWith("image/")) return "image";
  if (f.content_type === "application/pdf") return "pdf";
  return "other";
}

function fmtSize(bytes) {
  if (!bytes) return "PDF";
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

export default function Files() {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");
  const [preview, setPreview] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);

  // api is a module-level import (non-reactive); only stable state setters are referenced.
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/files");
      setFiles(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [setFiles, setLoading]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    let out = files;
    if (catFilter !== "all") out = out.filter((f) => f.category === catFilter);
    if (typeFilter !== "all") out = out.filter((f) => typeOf(f) === typeFilter);
    if (search.trim()) out = out.filter((f) => `${f.filename} ${f.task_title || ""}`.toLowerCase().includes(search.toLowerCase()));
    return out;
  }, [files, catFilter, typeFilter, search]);

  const counts = useMemo(() => {
    const c = {};
    for (const f of files) c[f.category] = (c[f.category] || 0) + 1;
    return c;
  }, [files]);

  const upload = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    setUploading(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(list.length > 1 ? `${list.length} files uploaded` : "File uploaded");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const confirmDelete = async () => {
    const f = deleteTarget;
    if (!f) return;
    setDeleting(true);
    try {
      if (f.generated && f.category === "spk") await api.delete(`/spk/${f.id.replace("gen-spk-", "")}`);
      else if (f.generated) await api.delete(`/invoices/${f.id.replace("gen-", "")}`);
      else await api.delete(`/attachments/${f.id}`);
      setFiles((p) => p.filter((x) => x.id !== f.id));
      toast.success(`"${f.filename}" dihapus`);
      setDeleteTarget(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeleting(false); }
  };

  const fileUrl = (f) => (f.pdf_url ? `${API_BASE}${f.pdf_url}` : `${API_BASE}/files/${f.storage_path}`);

  const openFile = (f) => {
    if (!f.generated && typeOf(f) === "other") {
      window.open(fileUrl(f), "_blank");
    } else {
      setPreview(f);
    }
  };

  let content;
  if (loading) {
    content = (
      <div className="flex justify-center py-20"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
    );
  } else if (visible.length === 0) {
    content = (
      <div className="text-center py-20 text-muted-foreground" data-testid="files-empty">
        <FolderOpen size={28} className="mx-auto mb-3 opacity-40" />
        <p className="text-sm">{files.length === 0 ? "Belum ada dokumen. Dokumen SPK, Invoice & Kuitansi akan muncul otomatis — atau upload file manual." : "No files match your filter."}</p>
      </div>
    );
  } else {
    content = (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="files-grid">
        {visible.map((f) => {
          const kind = typeOf(f);
          const cat = CAT_META[f.category] || CAT_META.upload;
          return (
            <div key={f.id} data-testid={`file-card-${f.id}`} className="group bg-card border border-border rounded-lg overflow-hidden hover:border-[#E31E24]/40 transition-colors">
              <button data-testid={`file-open-${f.id}`} onClick={() => openFile(f)} className="block w-full h-28 bg-muted/40 flex items-center justify-center overflow-hidden">
                {kind === "image" && !f.generated ? (
                  <img src={fileUrl(f)} alt={f.filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                ) : kind === "pdf" || f.generated ? (
                  <FileText size={30} className="text-[#E31E24]" />
                ) : (
                  <FileIcon size={30} className="text-muted-foreground" />
                )}
              </button>
              <div className="px-2.5 py-2">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className={cn("text-[9px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5", cat.cls)} data-testid={`file-category-${f.id}`}>{cat.label}</span>
                  {f.generated && (
                    <span className="text-[9px] text-muted-foreground flex items-center gap-0.5" title="Generated otomatis dari sistem"><Sparkles size={9} />Auto</span>
                  )}
                </div>
                <p className="text-xs font-medium truncate" title={f.filename}>{f.filename}</p>
                <p className="text-[10px] text-muted-foreground truncate">
                  {fmtSize(f.size)} · {f.source}{f.task_title ? `: ${f.task_title}` : ""}
                </p>
                <div className="flex items-center gap-1.5 mt-1.5">
                  {f.generated ? (
                    <span className="text-[10px] text-muted-foreground truncate flex-1">
                      Terbit {formatDistanceToNow(new Date(f.created_at), { addSuffix: true })}
                    </span>
                  ) : (
                    <>
                      <Avatar name={f.uploaded_by_name || "?"} userId={f.uploaded_by} size="sm" />
                      <span className="text-[10px] text-muted-foreground truncate flex-1">{f.uploaded_by_name} · {formatDistanceToNow(new Date(f.created_at), { addSuffix: true })}</span>
                    </>
                  )}
                  <a data-testid={`file-download-${f.id}`} href={f.generated ? `${fileUrl(f)}?download=1` : fileUrl(f)} target="_blank" rel="noreferrer"
                    className="h-6 w-6 flex md:hidden md:group-hover:flex items-center justify-center rounded text-muted-foreground hover:text-foreground transition-colors" title="Download">
                    <Download size={13} />
                  </a>
                  {(f.generated ? user?.role === "admin" : (f.uploaded_by === user?.id || user?.role === "admin")) && (
                    <button data-testid={`file-delete-${f.id}`} onClick={() => setDeleteTarget(f)}
                      className="h-6 w-6 flex md:hidden md:group-hover:flex items-center justify-center rounded text-muted-foreground hover:text-[#E31E24] transition-colors" title="Delete">
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 h-full overflow-y-auto" data-testid="files-page">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <span className="h-9 w-9 rounded-md bg-[#1B2A6B]/10 text-navy dark:text-blue-300 flex items-center justify-center"><FolderOpen size={17} /></span>
          <div>
            <h2 className="font-heading text-xl font-bold tracking-tight leading-tight">Files &amp; Documents</h2>
            <p className="text-xs text-muted-foreground" data-testid="files-count">
              {files.length} dokumen · {counts.spk || 0} SPK · {counts.invoice || 0} Invoice · {counts.receipt || 0} Kuitansi
            </p>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="files-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search files…"
              className="h-8 w-40 md:w-52 pl-7 pr-2 text-sm rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
          </div>
          <label data-testid="files-upload-label"
            className={cn("h-8 px-3 flex items-center gap-1.5 text-sm font-medium rounded-md bg-[#E31E24] hover:bg-[#C81A1F] text-white transition-colors cursor-pointer", uploading && "opacity-60 pointer-events-none")}>
            {uploading ? <UploadCloud size={15} className="animate-pulse" /> : <Plus size={15} />}
            {uploading ? "Uploading…" : "Upload"}
            <input data-testid="files-upload-input" type="file" multiple className="hidden" onChange={upload} disabled={uploading} />
          </label>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="flex gap-1 flex-wrap" data-testid="files-category-filter">
          {CATEGORY_FILTERS.map((c) => (
            <button key={c.key} data-testid={`files-cat-${c.key}`} onClick={() => setCatFilter(c.key)}
              className={cn("h-7 px-2.5 text-xs rounded-full border transition-colors",
                catFilter === c.key ? "bg-[#1B2A6B] text-white border-[#1B2A6B] font-medium" : "border-border text-muted-foreground hover:bg-muted")}>
              {c.label}{c.key !== "all" && counts[c.key] ? ` (${counts[c.key]})` : ""}
            </button>
          ))}
        </div>
        <div className="flex gap-1 p-0.5 bg-muted rounded-lg ml-auto" data-testid="files-type-filter">
          {TYPE_FILTERS.map((t) => (
            <button key={t.key} data-testid={`files-filter-${t.key}`} onClick={() => setTypeFilter(t.key)}
              className={cn("h-7 px-2.5 text-xs rounded-md transition-colors", typeFilter === t.key ? "bg-background shadow-sm font-medium" : "text-muted-foreground hover:text-foreground")}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {content}

      {preview && <FilePreviewModal file={preview} onClose={() => setPreview(null)} />}

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-sm bg-background" data-testid="file-delete-dialog">
          <DialogHeader><DialogTitle className="font-heading">Hapus File?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            File <span className="font-semibold text-foreground" data-testid="file-delete-name">{deleteTarget?.filename}</span> akan dihapus permanen dari daftar file.
          </p>
          <p className="text-xs text-[#E31E24] font-medium" data-testid="file-delete-warning">Tindakan ini tidak dapat dibatalkan.</p>
          {deleteTarget?.generated && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400" data-testid="file-delete-generated-note">
              Dokumen {deleteTarget?.source} ini dihapus permanen dari sistem beserta seluruh referensinya (hanya Admin).
            </p>
          )}
          <DialogFooter>
            <button data-testid="file-delete-cancel" onClick={() => setDeleteTarget(null)}
              className="h-9 px-4 text-sm rounded-md border border-border hover:bg-muted transition-colors">Batal</button>
            <button data-testid="file-delete-confirm" onClick={confirmDelete} disabled={deleting}
              className="h-9 px-4 text-sm rounded-md bg-[#E31E24] hover:bg-[#C81A1F] text-white font-medium transition-colors disabled:opacity-60 flex items-center gap-1.5">
              {deleting && <Loader2 size={13} className="animate-spin" />}Hapus Permanen
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
